/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// --- SVG Icons ---
const Icons = {
    Chart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Save: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>,
    Print: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
};

export default function Subjectdetail() {
    // Modal for resource suggestions/interventions
    const [showResourceModal, setShowResourceModal] = useState(false);
    const [resourceSuggestions, setResourceSuggestions] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);

    // Fetch resources for the current subject/grade
    const fetchResourceSuggestions = async () => {
        setLoadingResources(true);
        try {
            const token = localStorage.getItem('auth_token');
            const res = await axios.get((process.env.REACT_APP_API_URL || 'http://localhost/api') + '/fetch_library.php', {
                params: { grade: subjectGrade, subject: subjectname },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setResourceSuggestions(res.data.resources || []);
        } catch (e) {
            setResourceSuggestions([]);
        }
        setLoadingResources(false);
    };
    // Collapse state for Assessment Sections
    const [sectionsCollapsed, setSectionsCollapsed] = useState(false);
    // Collapse state for Import/Export and Add Student
    const [importExportCollapsed, setImportExportCollapsed] = useState(false);
    const [addStudentCollapsed, setAddStudentCollapsed] = useState(false);
        // --- Top 10 Students by Percentage ---
        const [showTop10, setShowTop10] = useState(false);
    const { id } = useParams();

    // --- State ---
    const [currentTerm, setCurrentTerm] = useState(1);
    const [students, setStudents] = useState([]);
    const [sections, setSections] = useState([]);
    const [sectionMarks, setSectionMarks] = useState([]);
    const [marksBeingEdited, setMarksBeingEdited] = useState({});
    const [subjectname, setSubjectName] = useState('Loading...');
    const [subjectGrade, setSubjectGrade] = useState('');
    const [subjectClass, setSubjectClass] = useState('');
    const [loading, setLoading] = useState(true);

    // UI State
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvProgress, setCsvProgress] = useState('');
    const [saveAllDisabled, setSaveAllDisabled] = useState(false);

    // Forms
    const [newStudent, setNewStudent] = useState({ firstName: '', lastName: '' });
    const [newSection, setNewSection] = useState({ section_name: '', weight: 1, max_mark: 100, category: '' });
    const [addingSection, setAddingSection] = useState(false);

    // Debounce State
    const [sectionWeights, setSectionWeights] = useState({});
    const [sectionMaxMarks, setSectionMaxMarks] = useState({});

    // Notes State
    const [notes, setNotes] = useState([]);
    const [showNotes, setShowNotes] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [isImportant, setIsImportant] = useState(false);
    const [dueDate, setDueDate] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    // Copy Section State
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [otherSubjects, setOtherSubjects] = useState([]);
    const [copyingSection, setCopyingSection] = useState(false);

    // Refs - Initialize once with useRef and useCallback
    const markInputRefs = useRef({});
    const weightTimersRef = useRef({});
    const maxMarkTimersRef = useRef({});
    const getMarkInputRef = useCallback((sectionId, studentId) => {
        if (!markInputRefs.current[sectionId]) {
            markInputRefs.current[sectionId] = {};
        }
        if (!markInputRefs.current[sectionId][studentId]) {
            markInputRefs.current[sectionId][studentId] = React.createRef();
        }
        return markInputRefs.current[sectionId][studentId];
    }, []);

    // --- Helpers ---
    const calculateLevel = useCallback((percentage) => {
        const rounded = Math.round(percentage);
        if (rounded >= 80) return 7;
        if (rounded >= 70) return 6;
        if (rounded >= 60) return 5;
        if (rounded >= 50) return 4;
        if (rounded >= 40) return 3;
        if (rounded >= 30) return 2;
        return 1;
    }, []);

    const levelToSymbol = useCallback((level) => {
        switch (level) {
            case 7: return 'A*';
            case 6: return 'A';
            case 5: return 'B';
            case 4: return 'C';
            case 3: return 'D';
            case 2: return 'E';
            case 1: return 'F';
            default: return level;
        }
    }, []);

    const formatGrade = useCallback((level) => {
        const gradeDisplayFormat = localStorage.getItem('gradeDisplayFormat') || 'levels';
        return gradeDisplayFormat === 'symbols' ? levelToSymbol(level) : level;
    }, [levelToSymbol]);

    // --- Calculate Stats Per Section (Optimized with Memoization) ---
    const getSectionStats = useCallback((section) => {
        const validMarks = [];
        const studentIds = new Set(); // Track unique students
        const sectionId = parseInt(section.id);

        for (const mark of sectionMarks) {
            if (parseInt(mark.section_id) === sectionId &&
                mark.mark !== '' &&
                mark.mark != null &&
                !isNaN(parseFloat(mark.mark))) {
                validMarks.push(parseFloat(mark.mark));
                studentIds.add(parseInt(mark.student_id)); // Add unique student ID
            }
        }

        if (validMarks.length === 0) return null;

        const count = studentIds.size; // Count unique students, not marks
        const max = Math.max(...validMarks);
        const min = Math.min(...validMarks);
        const sum = validMarks.reduce((a, b) => a + b, 0);
        const avg = sum / validMarks.length;
        const maxPossible = section.max_mark || 100;
        const avgPerc = (avg / maxPossible) * 100;

        return { count, max, min, avg, avgPerc };
    }, [sectionMarks]);

    // --- NOTES HELPERS ---
    const getDueDateStatus = useCallback((dueDate) => {
        if (!dueDate) return { status: 'future', label: '', daysLeft: null };
        const due = new Date(dueDate);
        const now = new Date();
        const diff = due - now;
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        
        if (days < 0) return { status: 'overdue', label: 'Overdue', daysLeft: Math.abs(days) };
        if (days === 0) return { status: 'due-today', label: 'Due Today', daysLeft: 0 };
        if (days <= 2) return { status: 'due-soon', label: `Due in ${days} day${days > 1 ? 's' : ''}`, daysLeft: days };
        if (days <= 7) return { status: 'due-week', label: `Due in ${days} days`, daysLeft: days };
        return { status: 'future', label: `Due in ${days} days`, daysLeft: days };
    }, []);

    const handleCreateNote = async (e) => {
        e.preventDefault();
        const cleanText = noteText.trim();
        if (!cleanText) return;

        setSavingNote(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'create',
                subject_id: id,
                note_text: cleanText,
                is_important: isImportant ? 1 : 0,
                due_date: dueDate || null
            });

            if (!res.data.success) {
                alert(res.data.error || 'Failed to save note');
            } else {
                setNoteText('');
                setIsImportant(false);
                setDueDate('');
                // Reload notes
                const notesRes = await axios.get(`${process.env.REACT_APP_API_URL}/subject_notes.php?subject_id=${id}`);
                    setNotes(notesRes.data.notes || []);
                    // Notify other views (notes page, dashboard) to refresh
                    try { window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { subject_id: id } })); } catch (e) {}
            }
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to save note');
        } finally {
            setSavingNote(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'delete',
                   note_id: noteId
            });
            if (res.data.success) {
                setNotes(notes.filter(n => n.id !== noteId));
                try { window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { subject_id: id } })); } catch (e) {}
            } else {
                   alert(res.data.error || 'Failed to delete note');
            }
        } catch (error) {
               alert(error.response?.data?.error || 'Failed to delete note');
        }
    };

    const handleToggleNoteDone = async (noteId, currentDone) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'update',
                note_id: noteId,
                is_done: currentDone ? 0 : 1
            });
            if (res.data.success) {
                setNotes(notes.map(n => n.id === noteId ? { ...n, is_done: currentDone ? 0 : 1 } : n));
                try { window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { subject_id: id } })); } catch (e) {}
            }
        } catch (error) {
            console.error('Failed to update note status');
        }
    };

    // --- COPY SECTION LOGIC ---
    const handleOpenCopyModal = async () => {
        setShowCopyModal(true);
        
        // Fetch other subjects owned by teacher
        try {
            const token = localStorage.getItem('auth_token');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_dashboard.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            // Filter to only other subjects (not current one)
            const others = (res.data.subjects || []).filter(s => String(s.subject_id) !== String(id));
            setOtherSubjects(others);
        } catch (error) {
            alert('Failed to load other subjects');
            setShowCopyModal(false);
        }
    };

    const handleCopyAllAssessments = async (targetSubjectId) => {
        if (!id) {
            alert('Error: Subject ID not found');
            return;
        }
        
        const sourceId = parseInt(id);
        if (isNaN(sourceId)) {
            alert('Error: Invalid subject ID');
            return;
        }
        
        if (!targetSubjectId) {
            alert('Error: Target subject ID not found');
            return;
        }
        
        setCopyingSection(true);
        try {
            const token = localStorage.getItem('auth_token');
            const payload = {
                source_subject_id: sourceId,
                target_subject_id: parseInt(targetSubjectId)
            };
            
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/duplicate_section.php`,
                JSON.stringify(payload),
                {
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (res.data.success) {
                alert(`${res.data.message}`);
                setShowCopyModal(false);
                // Could reload sections here if we want, but typically user navigates to target subject
            } else {
                alert(res.data.error || 'Copy failed');
            }
        } catch (error) {
            console.error('Copy error details:', error.response?.data || error.message);
            alert(error.response?.data?.error || 'Failed to copy assessments');
        } finally {
            setCopyingSection(false);
        }
    };

    // --- LOGIC: Grid Navigation (Optimized with cached indexes) ---
    const handleGridNavigation = useCallback((e, currentStudentId, currentSectionId) => {
        const allowedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
        if (!allowedKeys.includes(e.key)) return;

        e.preventDefault();

        const studentIndex = students.findIndex(s => String(s.student_id) === String(currentStudentId));
        const sectionIndex = sections.findIndex(s => String(s.id) === String(currentSectionId));

        let nextStudentId = currentStudentId;
        let nextSectionId = currentSectionId;

        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (studentIndex < students.length - 1) nextStudentId = students[studentIndex + 1].student_id;
        } else if (e.key === 'ArrowUp') {
            if (studentIndex > 0) nextStudentId = students[studentIndex - 1].student_id;
        } else if (e.key === 'ArrowRight') {
            if (sectionIndex < sections.length - 1) nextSectionId = sections[sectionIndex + 1].id;
        } else if (e.key === 'ArrowLeft') {
            if (sectionIndex > 0) nextSectionId = sections[sectionIndex - 1].id;
        }

        const ref = getMarkInputRef(nextSectionId, nextStudentId);
        if (ref && ref.current) {
            ref.current.focus();
            ref.current.select();
        }
    }, [students, sections, getMarkInputRef]);

    // --- LOGIC: Bulk Save ---
    const handleBulkSave = async () => {
        setSaveAllDisabled(true);
        try {
            // Separate marks into updates and deletes for better handling
            const marksToCreate = [];
            const marksToUpdate = [];
            const marksToDelete = [];

            // Only process marks that were actually edited (in marksBeingEdited)
            Object.entries(marksBeingEdited).forEach(([key, value]) => {
                const [studentId, sectionId] = key.split('-');
                const section = sections.find(s => String(s.id) === String(sectionId));
                const existingMark = sectionMarks.find(m =>
                    parseInt(m.student_id) === parseInt(studentId) &&
                    parseInt(m.section_id) === parseInt(sectionId)
                );

                // Check if mark is empty/null (should be deleted)
                if (value === '' || value === null || value === undefined) {
                    if (existingMark && existingMark.id) {
                        // Has ID - it exists in DB, needs deletion
                        marksToDelete.push(existingMark.id);
                    }
                    // Else: never saved, skip it
                } else {
                    // Has a value - create or update
                    const markData = {
                        student_id: parseInt(studentId),
                        section_id: parseInt(sectionId),
                        subject_id: id,
                        term: currentTerm,
                        mark: value,
                        max_mark: section ? section.max_mark : 100
                    };

                    if (existingMark && existingMark.id) {
                        markData.id = existingMark.id;
                        marksToUpdate.push(markData);
                    } else {
                        marksToCreate.push(markData);
                    }
                }
            });

            let totalUpdated = 0;

            // Handle deletions
            if (marksToDelete.length > 0) {
                for (const markId of marksToDelete) {
                    try {
                        await axios.delete(`${process.env.REACT_APP_API_URL}/section_marks.php?id=${markId}`);
                        totalUpdated++;
                    } catch (e) {
                        console.error(`Failed to delete mark ${markId}:`, e);
                    }
                }
                // Remove deleted marks from state
                setSectionMarks(prev => prev.filter(m => !marksToDelete.includes(m.id)));
            }

            // Handle creates and updates
            if (marksToCreate.length > 0 || marksToUpdate.length > 0) {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/save_bulk_marks.php`, {
                    create: marksToCreate,
                    update: marksToUpdate
                });

                if (response.data && response.data.success) {
                    // Count newly created and updated marks
                    totalUpdated += marksToCreate.length + marksToUpdate.length;

                    // Update marks with returned IDs for newly created marks
                    if (response.data.created_ids && Object.keys(response.data.created_ids).length > 0) {
                        setSectionMarks(prev => prev.map(m => {
                            const key = `${m.student_id}-${m.section_id}`;
                            const newId = response.data.created_ids[key];
                            return newId && !m.id ? { ...m, id: newId } : m;
                        }));
                    }
                } else {
                    alert('Save failed: ' + (response.data?.error || 'Unknown error'));
                }
            }

            alert(`Saved successfully! Updated/deleted ${totalUpdated} marks.`);
        } catch (error) {
            alert('Save error: ' + (error.response?.data?.error || error.message));
        } finally {
            setSaveAllDisabled(false);
        }
    };

    // --- LOGIC: Excel Upload ---
    const handleExcelUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setCsvUploading(true);
        setCsvProgress('Reading file...');

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    alert('Excel file is empty or missing data rows');
                    setCsvUploading(false); return;
                }

                const header = jsonData[0].map(h => String(h).trim());
                const sectionColumns = header.slice(2).filter(h => h && h.length > 0);

                if (sectionColumns.length === 0) {
                    alert('Excel must have at least one section column after First Name and Last Name');
                    setCsvUploading(false); return;
                }

                let workingSections = [...sections];
                const sectionIndices = {};
                let sectionsCreated = 0;

                setCsvProgress('Analyzing sections...');

                for (let colIdx = 0; colIdx < sectionColumns.length; colIdx++) {
                    const colName = sectionColumns[colIdx];
                    const excelColIndex = colIdx + 2;

                    let existingSection = workingSections.find(s =>
                        s.section_name.toLowerCase() === colName.toLowerCase()
                    );

                    if (existingSection) {
                        sectionIndices[existingSection.id] = excelColIndex;
                    } else {
                        try {
                            const res = await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                                subject_id: id,
                                term: currentTerm,
                                section_name: colName,
                                weight: 1,
                                max_mark: 100
                            });

                            if (res.data.success && res.data.section_id) {
                                const newSection = {
                                    id: res.data.section_id, section_name: colName, weight: 1, max_mark: 100
                                };
                                workingSections.push(newSection);
                                sectionIndices[newSection.id] = excelColIndex;
                                sectionsCreated++;
                            }
                        } catch (err) { }
                    }
                }

                if (Object.keys(sectionIndices).length === 0) {
                    alert('Could not match sections.'); setCsvUploading(false); return;
                }

                const totalRows = jsonData.length - 1;

                setCsvProgress(`Processing ${totalRows} students...`);

                const batchSize = 10;
                let successCount = 0;
                let studentsCreated = 0;
                for (let batchStart = 1; batchStart < jsonData.length; batchStart += batchSize) {
                    const batchEnd = Math.min(batchStart + batchSize, jsonData.length);
                    const batchPromises = [];

                    for (let i = batchStart; i < batchEnd; i++) {
                        const row = jsonData[i];
                        if (!row || row.length < 2) continue;

                        const firstName = String(row[0] || '').trim();
                        const lastName = String(row[1] || '').trim();
                        if (!firstName || !lastName) continue;

                        const processStudent = async () => {
                            try {
                                let student = students.find(s =>
                                    s.first_name.toLowerCase() === firstName.toLowerCase() &&
                                    s.last_name.toLowerCase() === lastName.toLowerCase()
                                );

                                if (!student) {
                                    try {
                                        const addRes = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                                            subject_id: id, first_name: firstName, last_name: lastName
                                        });
                                        if (addRes.data.success) {
                                            student = { student_id: addRes.data.student_id, first_name: firstName, last_name: lastName };
                                            studentsCreated++;
                                        }
                                    } catch (e) { return; }
                                }

                                if (student) {
                                    for (const [sectionId, colIdx] of Object.entries(sectionIndices)) {
                                        const rawMark = row[colIdx];
                                        const mark = parseFloat(rawMark);
                                        if (!isNaN(mark)) {
                                            const markObj = sectionMarks.find(m =>
                                                parseInt(m.student_id) === parseInt(student.student_id) &&
                                                parseInt(m.section_id) === parseInt(sectionId)
                                            );
                                            try {
                                                if (markObj && markObj.id) {
                                                    await axios.put(`${process.env.REACT_APP_API_URL}/section_marks.php`, { id: markObj.id, mark: mark });
                                                } else {
                                                    await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, { student_id: student.student_id, section_id: parseInt(sectionId), mark: mark });
                                                }
                                                successCount++;
                                            } catch (err) { }
                                        }
                                    }
                                }
                            } catch (rowError) { }
                        };
                        batchPromises.push(processStudent());
                    }
                    await Promise.all(batchPromises);
                    const processed = Math.min(batchEnd - 1, totalRows);
                    setCsvProgress(`Processing... ${processed}/${totalRows}`);
                    await new Promise(resolve => setTimeout(resolve, 100)); // Rate limit
                }

                setCsvUploading(false);
                setCsvProgress('');

                // Refresh data instead of full reload
                try {
                    const [updatedSecRes, updatedMarksRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`),
                        axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${currentTerm}`)
                    ]);
                    setSections(updatedSecRes.data.sections || []);
                    setSectionMarks(updatedMarksRes.data.marks || []);
                } catch (refreshErr) {
                    console.error('Error refreshing data:', refreshErr);
                }

                alert(`Import Result for Term ${currentTerm}:\nNew Sections: ${sectionsCreated}\nNew Students: ${studentsCreated}\nMarks Updated: ${successCount}`);

            } catch (error) {
                setCsvUploading(false);
                alert('Error processing Excel: ' + error.message);
            }
        };
    };

    // --- Computed Data (Optimized - Only recalculates when dependencies change) ---
    const studentsWithComputed = useMemo(() => {
        // Create a Map for O(1) lookups instead of O(n) finds
        const marksMap = new Map();
        for (const mark of sectionMarks) {
            marksMap.set(`${mark.student_id}-${mark.section_id}`, mark);
        }

        return students.map(student => {
            let weightedSum = 0;
            let weightSum = 0;
            let rawMarkSum = 0;
            let rawMaxSum = 0;
            let weightedScore = 0;
            let totalWeight = 0;

            const studentId = parseInt(student.student_id);

            for (const section of sections) {
                if (section.weight === 0) continue;

                const markObj = marksMap.get(`${studentId}-${section.id}`);
                const mark = (markObj && markObj.mark !== '' && markObj.mark != null)
                    ? parseFloat(markObj.mark)
                    : null;

                const max = section.max_mark ? parseFloat(section.max_mark) : 100;
                const weight = section.weight ? parseFloat(section.weight) : 0;

                if (mark !== null && max > 0) {
                    const percent = (mark / max) * 100;
                    weightedSum += percent * weight;
                    weightSum += weight;
                    rawMarkSum += mark;
                    rawMaxSum += max;

                    const pointsEarned = (mark / max) * weight;
                    weightedScore += pointsEarned;
                    totalWeight += weight;
                }
            }

            const average = weightSum > 0 ? weightedSum / weightSum : null;
            const level = average !== null ? calculateLevel(average) : null;

            return {
                ...student,
                average,
                level,
                rawMarkSum,
                rawMaxSum,
                weightedScore,
                totalWeight
            };
        });
    }, [students, sectionMarks, sections, calculateLevel]);

    const top10Students = useMemo(() => {
        return [...studentsWithComputed]
            .filter(s => s.rawMaxSum > 0)
            .sort((a, b) => (b.rawMarkSum / b.rawMaxSum) - (a.rawMarkSum / a.rawMaxSum))
            .slice(0, 10);
    }, [studentsWithComputed]);
    const top10Ids = useMemo(() => top10Students.map(s => s.student_id), [top10Students]);

    // --- Bottom 10 (At Risk) Learners ---
    const [showBottom10, setShowBottom10] = useState(false);
    // Learners at risk: 55% and below
    const atRiskLearners = useMemo(() => {
        return [...studentsWithComputed]
            .filter(s => s.rawMaxSum > 0 && (s.rawMarkSum / s.rawMaxSum) * 100 <= 55)
            .sort((a, b) => (a.rawMarkSum / a.rawMaxSum) - (b.rawMarkSum / b.rawMaxSum));
    }, [studentsWithComputed]);


    // --- Marks Map for O(1) lookup during render ---
    const marksMap = useMemo(() => {
        const map = new Map();
        for (const mark of sectionMarks) {
            map.set(`${mark.student_id}-${mark.section_id}`, mark);
        }
        return map;
    }, [sectionMarks]);

   


    // --- Excel Export ---
    const handleExportExcel = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();

        const nameLower = (subjectname || '').toLowerCase();
        const isHomeLanguage = nameLower.includes('home language') || nameLower.includes('huistaal');
        const passMark = isHomeLanguage ? 50 : 40;

        const localMarksMap = new Map();
        for (const mark of sectionMarks) {
            localMarksMap.set(`${mark.student_id}-${mark.section_id}`, mark);
        }

        const activeSections = sections.filter(s => parseFloat(s.weight) !== 0);
        const totalMaxMark = activeSections.reduce((sum, s) => sum + (parseFloat(s.max_mark) || 0), 0);

        const thinBorder = {
            top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
        };

        const fillGrey = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

        const styleRange = (sheet, startRow, endRow, startCol, endCol, style) => {
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol; c++) {
                    const cell = sheet.getCell(r, c);
                    if (style.font) cell.font = { ...(cell.font || {}), ...style.font };
                    if (style.fill) cell.fill = style.fill;
                    if (style.border) cell.border = style.border;
                    if (style.alignment) cell.alignment = { ...(cell.alignment || {}), ...style.alignment };
                }
            }
        };

        // ==================== SHEET 1: MARKS ====================
        const ws1 = workbook.addWorksheet(`Term ${currentTerm} Marks`);

        const categoryDefs = [];
        
        const categoryMap = {};
        activeSections.forEach(s => {
            const categoryName = (s.category || '').trim() || 'Uncategorized';
            if (!categoryMap[categoryName]) {
                categoryMap[categoryName] = {
                    name: categoryName,
                    totalMax: 0,
                    sectionIds: []
                };
                categoryDefs.push(categoryMap[categoryName]);
            }
            categoryMap[categoryName].totalMax += (parseFloat(s.max_mark) || 0);
            categoryMap[categoryName].sectionIds.push(s.id);
        });

        // Compute dimensions upfront (needed for info rows)
        
        const categoryStartCol = 4 + sections.length * 3;
        const overallStartCol = categoryStartCol + categoryDefs.length * 3;
        const ws1TotalCols = overallStartCol + 2;
        const midCol = Math.ceil(ws1TotalCols / 2);

        // Info row 1: Subject | Term
        const infoRow1 = Array(ws1TotalCols).fill('');
        infoRow1[0] = 'Subject:';
        infoRow1[1] = subjectname;
        infoRow1[midCol - 1] = 'Term:';
        infoRow1[midCol] = String(currentTerm);
        ws1.addRow(infoRow1);  // row 1

        // Info row 2: Educator | Grade | Class
        const infoRow2 = Array(ws1TotalCols).fill('');
        infoRow2[0] = 'Educator:';
        infoRow2[midCol - 1] = 'Grade:';
        infoRow2[midCol] = subjectGrade;
        infoRow2[midCol + 1] = 'Class:';
        infoRow2[midCol + 2] = subjectClass;
        ws1.addRow(infoRow2);  // row 2

        const headerRow0 = ['No', 'Surname', 'Name'];
        sections.forEach(s => {
            headerRow0.push(`${s.section_name} (${s.max_mark})`);
            headerRow0.push('');
            headerRow0.push('');
        });
        categoryDefs.forEach(cat => {
            headerRow0.push(`TOTAL ${cat.name.toUpperCase()} (${cat.totalMax})`);
            headerRow0.push('');
            headerRow0.push('');
        });
        headerRow0.push(`OVERALL TOTAL (${totalMaxMark})`);
        headerRow0.push('');
        headerRow0.push('');

        const headerRow1 = ['', '', ''];
        sections.forEach(s => {
            headerRow1.push(s.max_mark);
            headerRow1.push('%');
            headerRow1.push('Lvl');
        });
        categoryDefs.forEach(cat => {
            headerRow1.push(cat.totalMax);
            headerRow1.push('%');
            headerRow1.push('Lvl');
        });
        headerRow1.push(totalMaxMark);
        headerRow1.push('%');
        headerRow1.push('Lvl');

        ws1.addRow(headerRow0);  // row 3
        ws1.addRow(headerRow1);  // row 4

        const secStats = {};
        activeSections.forEach(s => {
            secStats[s.id] = {
                marks: [],
                levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
                passedCount: 0,
                failedCount: 0
            };
        });

        const catStats = {};
        categoryDefs.forEach(cat => {
            catStats[cat.name] = {
                percentages: [],
                levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
                passedCount: 0,
                failedCount: 0
            };
        });

        const overallRawData = [];
        let overallPassedCount = 0;
        let overallFailedCount = 0;
        const overallLevelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };

        studentsWithComputed.forEach((student, idx) => {
            const row = [idx + 1, student.last_name, student.first_name];
            const studentId = parseInt(student.student_id);
            const studentCatTotals = {};
            categoryDefs.forEach(cat => {
                studentCatTotals[cat.name] = { markSum: 0, maxSum: 0, hasMark: false };
            });

            sections.forEach(section => {
                const markObj = localMarksMap.get(`${studentId}-${section.id}`);
                const markVal = (markObj && markObj.mark !== '' && markObj.mark != null)
                    ? parseFloat(markObj.mark)
                    : null;
                const max = parseFloat(section.max_mark) || 100;

                row.push(markVal !== null ? markVal : '');

                if (markVal !== null && max > 0) {
                    const p = (markVal / max) * 100;
                    row.push(Math.round(p));
                    const level = calculateLevel(p);
                    row.push(formatGrade(level));

                    if (parseFloat(section.weight) !== 0 && secStats[section.id]) {
                        secStats[section.id].marks.push(markVal);
                        secStats[section.id].levelDist[level]++;
                        if (Math.round(p) >= passMark) secStats[section.id].passedCount++;
                        else secStats[section.id].failedCount++;

                        const categoryName = (section.category || '').trim() || 'Uncategorized';
                        if (categoryName && studentCatTotals[categoryName]) {
                            studentCatTotals[categoryName].markSum += markVal;
                            studentCatTotals[categoryName].maxSum += max;
                            studentCatTotals[categoryName].hasMark = true;
                        }
                    }
                } else {
                    row.push('');
                    row.push('');
                }
            });

            categoryDefs.forEach(cat => {
                const catTotal = studentCatTotals[cat.name];
                if (!catTotal.hasMark || catTotal.maxSum <= 0) {
                    row.push('');
                    row.push('');
                    row.push('');
                    return;
                }

                const catPerc = (catTotal.markSum / catTotal.maxSum) * 100;
                const catLevel = calculateLevel(catPerc);

                row.push(Number(catTotal.markSum).toFixed(1));
                row.push(Math.round(catPerc));
                row.push(formatGrade(catLevel));

                if (catStats[cat.name]) {
                    catStats[cat.name].percentages.push(catPerc);
                    catStats[cat.name].levelDist[catLevel]++;
                    if (Math.round(catPerc) >= passMark) catStats[cat.name].passedCount++;
                    else catStats[cat.name].failedCount++;
                }
            });

            const rawTotal = student.rawMarkSum;
            const rawMax = student.rawMaxSum;
            const overallPerc = rawMax > 0 ? (rawTotal / rawMax) * 100 : null;

            row.push(rawMax > 0 ? Number(rawTotal).toFixed(1) : '');
            row.push(overallPerc !== null ? Math.round(overallPerc) : '');
            const overallLevel = overallPerc !== null ? calculateLevel(overallPerc) : null;
            row.push(overallLevel ? formatGrade(overallLevel) : '');

            if (overallPerc !== null) {
                overallRawData.push({ markSum: rawTotal, maxSum: rawMax, perc: overallPerc });
                overallLevelDist[overallLevel]++;
                if (Math.round(overallPerc) >= passMark) overallPassedCount++;
                else overallFailedCount++;
            }

            ws1.addRow(row);
        });

        // Info row merges
        if (midCol > 2) ws1.mergeCells(1, 2, 1, midCol - 1);
        if (midCol + 1 < ws1TotalCols) ws1.mergeCells(1, midCol + 1, 1, ws1TotalCols);
        if (midCol > 2) ws1.mergeCells(2, 2, 2, midCol - 1);
        if (midCol + 3 <= ws1TotalCols) ws1.mergeCells(2, midCol + 3, 2, ws1TotalCols);

        // Column header merges (rows 3-4)
        ws1.mergeCells(3, 1, 4, 1);
        ws1.mergeCells(3, 2, 4, 2);
        ws1.mergeCells(3, 3, 4, 3);
        sections.forEach((s, i) => {
            const sc = 4 + i * 3;
            ws1.mergeCells(3, sc, 3, sc + 2);
        });
        categoryDefs.forEach((cat, i) => {
            const cc = categoryStartCol + i * 3;
            ws1.mergeCells(3, cc, 3, cc + 2);
        });
        ws1.mergeCells(3, overallStartCol, 3, overallStartCol + 2);

        ws1.columns = [
            { width: 5 },
            { width: 18 },
            { width: 16 },
            ...sections.flatMap(() => ([{ width: 10 }, { width: 6 }, { width: 6 }])),
            ...categoryDefs.flatMap(() => ([{ width: 12 }, { width: 6 }, { width: 6 }])),
            { width: 12 },
            { width: 6 },
            { width: 6 }
        ];

        const ws1LastRow = ws1.rowCount;

        // Style info rows (1-2)
        styleRange(ws1, 1, 2, 1, ws1TotalCols, {
            fill: fillGrey('FFFAFAFA'),
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });
        styleRange(ws1, 1, 2, 1, 1, { font: { bold: true } });
        styleRange(ws1, 1, 1, midCol, midCol, { font: { bold: true } });
        styleRange(ws1, 2, 2, midCol, midCol, { font: { bold: true } });
        styleRange(ws1, 2, 2, midCol + 2, midCol + 2, { font: { bold: true } });
        ws1.getRow(1).height = 22;
        ws1.getRow(2).height = 22;

        // Style column header rows (3-4)
        styleRange(ws1, 3, 3, 1, ws1TotalCols, {
            font: { bold: true },
            fill: fillGrey('FFD9D9D9'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws1, 4, 4, 1, ws1TotalCols, {
            font: { bold: true },
            fill: fillGrey('FFEFEFEF'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        if (ws1LastRow >= 5) {
            styleRange(ws1, 5, ws1LastRow, 1, ws1TotalCols, {
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
            styleRange(ws1, 5, ws1LastRow, 2, 3, {
                border: thinBorder,
                alignment: { horizontal: 'left', vertical: 'middle' }
            });
            styleRange(ws1, 3, ws1LastRow, overallStartCol, ws1TotalCols, {
                fill: fillGrey('FFF5F5F5'),
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
        }

        // ==================== SHEET 2: COMBINED STATS ====================
        const ws2 = workbook.addWorksheet(`Term ${currentTerm} Statistics`);

        const analysisLabel = activeSections.map(s => s.section_name).join(', ');
        ws2.addRow(['STATISTIC AND DIAGNOSTIC ANALYSIS OF MARKS']);
        ws2.addRow([]);
        ws2.addRow(['SUBJECT:', subjectname, '', '', 'MARK:', totalMaxMark]);
        ws2.addRow(['EXAMINER:', '', '', '', 'GRADE:', subjectGrade]);
        ws2.addRow(['DATE:', '', '', '', 'TERM:', currentTerm]);
        ws2.addRow(['ANALYSIS:', analysisLabel]);
        ws2.addRow([]);

        ws2.addRow([
            'Section', 'Total in class', 'Total written', 'Class average',
            'Highest mark', 'Lowest mark', 'Total failed', '% Failures',
            'Total that passed', '% Passed',
            '0 - 29', '30 - 39', '40 - 49', '50 - 59', '60 - 69', '70 - 79', '80 - 100',
            'Total (1-7 = Total/Total written)'
        ]);

        const sectionsByCategory = {};
        const categoryOrder = [];
        activeSections.forEach(section => {
            const category = (section.category || '').trim() || 'Uncategorized';
            if (!sectionsByCategory[category]) {
                sectionsByCategory[category] = [];
                categoryOrder.push(category);
            }
            sectionsByCategory[category].push(section);
        });

        const categoryTotalRows = [];

        categoryOrder.forEach(category => {
            const groupSections = sectionsByCategory[category];

            groupSections.forEach(section => {
                const stats = secStats[section.id];
                const max = parseFloat(section.max_mark) || 100;
                const tw = stats.marks.length;
                const avgPerc = tw > 0
                    ? stats.marks.reduce((a, b) => a + (b / max) * 100, 0) / tw
                    : null;
                const highest = tw > 0 ? Math.max(...stats.marks) : '';
                const lowest = tw > 0 ? Math.min(...stats.marks) : '';
                const total17 = Object.values(stats.levelDist).reduce((a, b) => a + b, 0);
                const failP = tw > 0 ? Math.round((stats.failedCount / tw) * 100) + '%' : '0%';
                const passP = tw > 0 ? Math.round((stats.passedCount / tw) * 100) + '%' : '0%';

                ws2.addRow([
                    `${section.section_name} (${max})`,
                    students.length,
                    tw,
                    avgPerc !== null ? avgPerc.toFixed(1) + '%' : '',
                    highest,
                    lowest,
                    stats.failedCount,
                    failP,
                    stats.passedCount,
                    passP,
                    stats.levelDist[1], stats.levelDist[2], stats.levelDist[3],
                    stats.levelDist[4], stats.levelDist[5], stats.levelDist[6], stats.levelDist[7],
                    tw > 0 ? `${total17}/${tw}` : ''
                ]);
            });

            const mappedName = category === 'Uncategorized' ? 'Uncategorized' : category;
            const currentCatStats = catStats[mappedName] || {
                percentages: [],
                levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
                passedCount: 0,
                failedCount: 0
            };
            const categoryTotalWritten = currentCatStats.percentages.length;
            const categoryAvgPerc = categoryTotalWritten > 0
                ? currentCatStats.percentages.reduce((a, b) => a + b, 0) / categoryTotalWritten
                : null;
            const categoryHighestPerc = categoryTotalWritten > 0 ? Math.round(Math.max(...currentCatStats.percentages)) + '%' : '';
            const categoryLowestPerc = categoryTotalWritten > 0 ? Math.round(Math.min(...currentCatStats.percentages)) + '%' : '';
            const categoryTotal17 = Object.values(currentCatStats.levelDist).reduce((a, b) => a + b, 0);
            const categoryFailP = categoryTotalWritten > 0 ? Math.round((currentCatStats.failedCount / categoryTotalWritten) * 100) + '%' : '0%';
            const categoryPassP = categoryTotalWritten > 0 ? Math.round((currentCatStats.passedCount / categoryTotalWritten) * 100) + '%' : '0%';

            ws2.addRow([
                `TOTAL ${category.toUpperCase()}`,
                students.length,
                categoryTotalWritten,
                categoryAvgPerc !== null ? categoryAvgPerc.toFixed(1) + '%' : '',
                categoryHighestPerc,
                categoryLowestPerc,
                currentCatStats.failedCount,
                categoryFailP,
                currentCatStats.passedCount,
                categoryPassP,
                currentCatStats.levelDist[1], currentCatStats.levelDist[2], currentCatStats.levelDist[3],
                currentCatStats.levelDist[4], currentCatStats.levelDist[5], currentCatStats.levelDist[6], currentCatStats.levelDist[7],
                categoryTotalWritten > 0 ? `${categoryTotal17}/${categoryTotalWritten}` : ''
            ]);
            categoryTotalRows.push(ws2.rowCount);
        });

        const totalWrittenOverall = overallRawData.length;
        const overallClassAvgPerc = totalWrittenOverall > 0
            ? overallRawData.reduce((a, b) => a + b.perc, 0) / totalWrittenOverall
            : null;
        const allRawSums = overallRawData.map(r => r.markSum);
        const highestOverall = allRawSums.length > 0 ? Math.max(...allRawSums) : '';
        const lowestOverall = allRawSums.length > 0 ? Math.min(...allRawSums) : '';
        const overallTotal17 = Object.values(overallLevelDist).reduce((a, b) => a + b, 0);
        const overallFailP = totalWrittenOverall > 0 ? Math.round((overallFailedCount / totalWrittenOverall) * 100) + '%' : '0%';
        const overallPassP = totalWrittenOverall > 0 ? Math.round((overallPassedCount / totalWrittenOverall) * 100) + '%' : '0%';

        ws2.addRow([
            `OVERALL TOTAL (${totalMaxMark})`,
            students.length,
            totalWrittenOverall,
            overallClassAvgPerc !== null ? overallClassAvgPerc.toFixed(1) + '%' : '',
            highestOverall,
            lowestOverall,
            overallFailedCount,
            overallFailP,
            overallPassedCount,
            overallPassP,
            overallLevelDist[1], overallLevelDist[2], overallLevelDist[3],
            overallLevelDist[4], overallLevelDist[5], overallLevelDist[6], overallLevelDist[7],
            totalWrittenOverall > 0 ? `${overallTotal17}/${totalWrittenOverall}` : ''
        ]);
        const overallTotalRow = ws2.rowCount;

        ws2.addRow([]);
        ws2.addRow(['DIAGNOSTIC ANALYSIS: (High frequency errors that were made in questions)']);
        ws2.addRow([]);
        ws2.addRow(['Problem area', '', '', '', 'Intervention', '', '', '', '', '', 'Time', '']);
        for (let i = 0; i < 4; i++) {
            ws2.addRow(['', '', '', '', '', '', '', '', '', '', '', '']);
        }

        ws2.addRow([]);
        ws2.addRow(['Signature of Teacher:', '', '', '', '', '', '', '', '', 'Date:', '', '', '', '']);
        ws2.addRow(['Signature of Departmental Head:', '', '', '', '', '', '', '', '', 'Date:', '', '', '', '']);

        ws2.mergeCells(1, 1, 1, 18);
        ws2.mergeCells(6, 2, 6, 18);
        ws2.columns = [
            { width: 24 }, { width: 12 }, { width: 12 }, { width: 13 }, { width: 12 }, { width: 12 },
            { width: 11 }, { width: 11 }, { width: 13 }, { width: 10 },
            { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 },
            { width: 18 }
        ];

        const tableHeaderRow = 8;
        const tableFirstDataRow = 9;
        const tableLastDataRow = overallTotalRow;
        const diagTitleRow = tableLastDataRow + 2;
        const diagHeaderRow = tableLastDataRow + 4;
        const diagLastRow = diagHeaderRow + 4;
        const sigRow1 = diagLastRow + 2;
        const sigRow2 = diagLastRow + 3;

        // Diagnostic section merges
        ws2.mergeCells(diagTitleRow, 1, diagTitleRow, 18);
        ws2.mergeCells(diagHeaderRow, 1, diagHeaderRow, 4);
        ws2.mergeCells(diagHeaderRow, 5, diagHeaderRow, 10);
        ws2.mergeCells(diagHeaderRow, 11, diagHeaderRow, 12);
        for (let di = 1; di <= 4; di++) {
            ws2.mergeCells(diagHeaderRow + di, 1, diagHeaderRow + di, 4);
            ws2.mergeCells(diagHeaderRow + di, 5, diagHeaderRow + di, 10);
            ws2.mergeCells(diagHeaderRow + di, 11, diagHeaderRow + di, 12);
            ws2.getRow(diagHeaderRow + di).height = 45;
        }

        // Signature row merges
        [sigRow1, sigRow2].forEach(r => {
            ws2.mergeCells(r, 1, r, 2);
            ws2.mergeCells(r, 3, r, 8);
            ws2.mergeCells(r, 11, r, 14);
        });

        styleRange(ws2, 1, 1, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFD9D9D9'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws2, 3, 6, 1, 1, {
            font: { bold: true },
            fill: fillGrey('FFEFEFEF'),
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });
        styleRange(ws2, 3, 6, 2, 18, {
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });

        styleRange(ws2, tableHeaderRow, tableHeaderRow, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFE5E5E5'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        if (tableLastDataRow >= tableFirstDataRow) {
            styleRange(ws2, tableFirstDataRow, tableLastDataRow, 1, 18, {
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
            styleRange(ws2, tableFirstDataRow, tableLastDataRow, 1, 1, {
                border: thinBorder,
                alignment: { horizontal: 'left', vertical: 'middle' }
            });
        }

        categoryTotalRows.forEach(r => {
            styleRange(ws2, r, r, 1, 18, {
                font: { bold: true },
                fill: fillGrey('FFF9F9F9'),
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
            styleRange(ws2, r, r, 1, 1, {
                font: { bold: true },
                fill: fillGrey('FFF9F9F9'),
                border: thinBorder,
                alignment: { horizontal: 'left', vertical: 'middle' }
            });
        });

        styleRange(ws2, overallTotalRow, overallTotalRow, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFF5F5F5'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws2, diagTitleRow, diagTitleRow, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFEFEFEF'),
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });

        styleRange(ws2, diagHeaderRow, diagHeaderRow, 1, 12, {
            font: { bold: true },
            fill: fillGrey('FFE5E5E5'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws2, diagHeaderRow + 1, diagLastRow, 1, 12, {
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'top' }
        });

        // Signature rows styling
        [sigRow1, sigRow2].forEach(r => {
            ws2.getRow(r).height = 32;
            ws2.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'bottom' };
            ws2.getCell(r, 3).border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
            ws2.getCell(r, 10).font = { bold: false };
            ws2.getCell(r, 10).alignment = { horizontal: 'left', vertical: 'bottom' };
            ws2.getCell(r, 11).border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
        });

        const filename = `${(subjectname || 'Subject').replace(/\s+/g, '_')}_${(subjectClass || 'Class').replace(/\s+/g, '_')}_Term_${currentTerm}_Report.xlsx`;
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
    }, [studentsWithComputed, sections, sectionMarks, calculateLevel, formatGrade, currentTerm, subjectname, subjectClass, subjectGrade, students.length]);

    const handleDownloadTemplate = async () => {
        if (!sections.length && !students.length) { alert('Add sections or students first.'); return; }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Template');

        const headers = ['First Name', 'Last Name', ...sections.map(s => s.section_name)];
        worksheet.addRow(headers);

        students.forEach(s => {
            const row = [s.first_name, s.last_name];
            sections.forEach(() => row.push(''));
            worksheet.addRow(row);
        });

        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true };
        headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
        headerRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFEFEFEF' }
            };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
                left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
                bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
                right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
            };
        });

        worksheet.columns = [
            { width: 18 },
            { width: 18 },
            ...sections.map(() => ({ width: 14 }))
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        saveAs(blob, `Template_${(subjectname || 'Subject').replace(/\s+/g, '_')}_Term${currentTerm}.xlsx`);
    };

    // --- Section Management Handlers ---
    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!newSection.section_name.trim()) return alert('Name required');
        setAddingSection(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                subject_id: id,
                term: currentTerm,
                ...newSection
            });
            setNewSection({ section_name: '', weight: 1, max_mark: 100, category: '' });
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`);
            setSections(res.data.sections || []);
        } catch (e) { alert('Failed'); }
        finally { setAddingSection(false); }
    };

    const handleUpdateSection = (id, field, val) => {
        if (field === 'weight') setSectionWeights(p => ({ ...p, [id]: val }));
        if (field === 'max_mark') setSectionMaxMarks(p => ({ ...p, [id]: val }));

        const timerRef = field === 'weight' ? weightTimersRef : maxMarkTimersRef;
        if (timerRef.current[id]) clearTimeout(timerRef.current[id]);

        timerRef.current[id] = setTimeout(async () => {
            try {
                const section = sections.find(s => s.id === id);
                if (!section) return;

                await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                    id,
                    section_name: section.section_name,
                    weight: field === 'weight' ? val : section.weight,
                    max_mark: field === 'max_mark' ? val : section.max_mark,
                    category: section.category || ''
                });

                setSections(prev => prev.map(s =>
                    s.id === id ? { ...s, [field]: parseFloat(val) } : s
                ));

                if (field === 'weight') {
                    setSectionWeights(prev => { const u = { ...prev }; delete u[id]; return u; });
                }
                if (field === 'max_mark') {
                    setSectionMaxMarks(prev => { const u = { ...prev }; delete u[id]; return u; });
                }

            } catch (e) {
                alert('Update failed. Check your connection.');
            }
        }, 1500);
    };

    const SECTION_CATEGORIES = ['', 'Test', 'Assignment','Investigation','Project','Exam','Practical','Oral','Transactional Writing','Essay','Other'];

    const handleUpdateSectionCategory = async (sectionId, val) => {
        try {
            const section = sections.find(s => s.id === sectionId);
            if (!section) return;
            await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                id: sectionId,
                section_name: section.section_name,
                weight: section.weight,
                max_mark: section.max_mark,
                category: val
            });
            setSections(prev => prev.map(s => s.id === sectionId ? { ...s, category: val } : s));
        } catch (e) {
            alert('Update failed. Check your connection.');
        }
    };

    const handleDeleteSection = async (sid, name) => {
        if (!window.confirm(`Delete ${name}?`)) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/task_sections.php?id=${sid}`);
            setSections(prev => prev.filter(s => s.id !== sid));
        } catch (e) { alert('Error deleting'); }
    };

    // --- Mark Handlers (Optimized) ---
    const handleSectionMarkChange = useCallback((studentId, sectionId, value) => {
        const key = `${studentId}-${sectionId}`;
        let sanitized = value.replace(/[^0-9.]/g, '');

        // Allow empty string (for deletion)
        if (sanitized === '') {
            setMarksBeingEdited(prev => ({ ...prev, [key]: '' }));
            setSectionMarks(prev => {
                const idx = prev.findIndex(m => parseInt(m.student_id) === parseInt(studentId) && parseInt(m.section_id) === parseInt(sectionId));
                if (idx !== -1) {
                    const newMarks = [...prev];
                    newMarks[idx] = { ...newMarks[idx], mark: '' };
                    return newMarks;
                }
                return prev;
            });
            return;
        }

        // Validate and cap the value
        if (!isNaN(sanitized)) {
            let num = parseFloat(sanitized);
            const section = sections.find(s => s.id === sectionId);
            const max = section?.max_mark || 100;
            if (num > max) num = max;
            sanitized = String(num);
        }

        setMarksBeingEdited(prev => ({ ...prev, [key]: sanitized }));

        setSectionMarks(prev => {
            const idx = prev.findIndex(m => parseInt(m.student_id) === parseInt(studentId) && parseInt(m.section_id) === parseInt(sectionId));
            if (idx !== -1 && prev[idx].mark === sanitized) return prev; // Prevent unnecessary updates

            const newMarks = [...prev];
            if (idx !== -1) {
                newMarks[idx] = { ...newMarks[idx], mark: sanitized };
            } else {
                newMarks.push({ student_id: parseInt(studentId), section_id: parseInt(sectionId), mark: sanitized });
            }
            return newMarks;
        });
    }, [sections]);

    const handleMarkBlur = useCallback(async (studentId, sectionId) => {
        const key = `${studentId}-${sectionId}`;
        const val = marksBeingEdited[key];
        if (val !== undefined) {
            try {
                // Find the existing mark record
                const markObj = sectionMarks.find(m =>
                    parseInt(m.student_id) === parseInt(studentId) &&
                    parseInt(m.section_id) === parseInt(sectionId)
                );

                if (val === '' || val === null) {
                    // Empty value - DELETE the mark if it exists
                    if (markObj && markObj.id) {
                        await axios.delete(`${process.env.REACT_APP_API_URL}/section_marks.php?id=${markObj.id}`);
                        // Remove from state
                        setSectionMarks(prev => prev.filter(m => m.id !== markObj.id));
                    }
                } else {
                    // Non-empty value - POST or PUT
                    if (markObj && markObj.id) {
                        // Update existing mark
                        await axios.put(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                            id: markObj.id,
                            mark: val
                        });
                    } else {
                        // Create new mark
                        const response = await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                            student_id: studentId,
                            section_id: sectionId,
                            mark: val
                        });
                        // Update the mark in state with the new ID from response
                        if (response.data.id) {
                            setSectionMarks(prev => {
                                const idx = prev.findIndex(m =>
                                    parseInt(m.student_id) === parseInt(studentId) &&
                                    parseInt(m.section_id) === parseInt(sectionId)
                                );
                                if (idx !== -1) {
                                    const newMarks = [...prev];
                                    newMarks[idx] = { ...newMarks[idx], id: response.data.id };
                                    return newMarks;
                                }
                                return prev;
                            });
                        }
                    }
                }

                setMarksBeingEdited(prev => {
                    const u = { ...prev };
                    delete u[key];
                    return u;
                });
            } catch (e) {
                console.error('Mark save error:', e);
                alert('Error saving mark: ' + (e.response?.data?.error || e.message));
            }
        }
    }, [marksBeingEdited, sectionMarks]);

    // --- Student Handlers ---
    const handleAddStudent = async () => {
        if (!newStudent.firstName) return alert('Name required');
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                subject_id: id, first_name: newStudent.firstName, last_name: newStudent.lastName
            });
            if (res.data.success) {
                const newStudentData = {
                    student_id: res.data.student_id,
                    first_name: newStudent.firstName,
                    last_name: newStudent.lastName,
                    level: null,
                    average: null
                };
                setStudents(prev => [...prev, newStudentData]);
                setNewStudent({ firstName: '', lastName: '' });
                alert('Student added successfully!');
            } else {
                alert('Failed to add student');
            }
        } catch (e) { alert('Error adding student: ' + e.message); }
    };

    const handleDeleteStudent = async (sid, name) => {
        if (!window.confirm(`Remove student ${name || ''}?`)) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_student.php`, { student_id: sid, subject_id: id });
            setStudents(prev => prev.filter(s => s.student_id !== sid));
        } catch (e) { alert('Error'); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('Delete ALL DATA?')) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_all_students.php`, { subject_id: id });
            setStudents([]);
            setSectionMarks([]);
            alert('All students and marks deleted successfully!');
        } catch (e) { alert('Error deleting: ' + e.message); }
    };

    // --- Initial Load (Optimized) ---
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [classRes, secRes, marksRes, notesRes] = await Promise.allSettled([
                    axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${currentTerm}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/subject_notes.php?subject_id=${id}`)
                ]);

                // Set students data
                const classData = classRes.status === 'fulfilled' ? classRes.value.data : {};
                const studentsData = (classData.students || []).map(s => ({
                    ...s,
                    level: s.average ? calculateLevel(s.average) : null
                }));
                setStudents(studentsData);
                setSubjectName(classData.subject_name || 'Subject');
                setSubjectGrade(classData.grade || '');
                setSubjectClass(classData.class_name || '');

                // Set sections and weights
                const secData = secRes.status === 'fulfilled' ? secRes.value.data : { sections: [] };
                const sectionsData = secData.sections || [];
                setSections(sectionsData);

                const weights = {};
                const maxes = {};
                sectionsData.forEach(s => {
                    weights[s.id] = s.weight;
                    maxes[s.id] = s.max_mark;
                });
                setSectionWeights(weights);
                setSectionMaxMarks(maxes);

                // Set marks
                const marksData = marksRes.status === 'fulfilled' ? marksRes.value.data : { marks: [] };
                setSectionMarks(marksData.marks || []);

                // Set notes (gracefully handle failure)
                const notesData = notesRes.status === 'fulfilled' ? notesRes.value.data : { notes: [] };
                setNotes(notesData.notes || []);

            } catch (e) {
                console.error('Load error:', e);
            }
            setLoading(false);
        };
        init();
    }, [id, currentTerm, calculateLevel]);

    return (
        <div className="subject-detail-wrapper">
            <style>{`
                :root {
                    --primary-green: #4CAF50;
                    --dark-green: #388E3C;
                    --light-green: #E8F5E9;
                    --text-dark: #2c3e50;
                    --text-light: #7f8c8d;
                    --border-color: #ecf0f1;
                    --card-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
                }

                body { background-color: #f8f9fa; font-family: 'Inter', -apple-system, sans-serif; color: var(--text-dark); }
                
                .subject-detail-wrapper {
                    padding: 2rem;
                    margin-left: 220px; 
                    max-width: 1600px;
                }

                .page-header {
                    background: white;
                    padding: 1.5rem 2rem;
                    border-radius: 12px;
                    box-shadow: var(--card-shadow);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                }
                .page-header h1 { margin: 0; font-size: 1.5rem; font-weight: 700; color: var(--text-dark); }
                .badge { background: var(--light-green); color: var(--dark-green); padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.85rem; margin-left: 10px; }

                .term-tabs {
                    display: flex;
                    gap: 10px;
                    background: #f1f3f5;
                    padding: 5px;
                    border-radius: 25px;
                }
                .term-tab {
                    padding: 8px 24px;
                    border: none;
                    border-radius: 20px;
                    font-weight: 600;
                    color: #7f8c8d;
                    background: transparent;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.9rem;
                }
                .term-tab.active {
                    background: white;
                    color: var(--primary-green);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                .term-tab:hover:not(.active) {
                    color: var(--text-dark);
                }

                .management-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }
                .modern-card { background: white; border-radius: 12px; padding: 1.5rem; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); }
                .card-title { font-size: 1.1rem; font-weight: 600; margin-top: 0; margin-bottom: 1rem; color: var(--text-dark); border-bottom: 2px solid var(--light-green); padding-bottom: 0.5rem; display: inline-block; }
                .modern-input { width: 100%; padding: 0.6rem; border: 1px solid #dfe6e9; border-radius: 6px; font-size: 0.9rem; box-sizing: border-box; }
                .compact-notes-card { min-width: 0; box-sizing: border-box; }
                .compact-notes-card .compact-notes-form { display: flex; flex-direction: column; gap: 8px; }
                .compact-notes-card .compact-notes-form > .modern-input { margin-bottom: 4px; }
                .compact-notes-card .compact-datetime { padding: 8px 10px; border: 1px solid #e0e0e0; border-radius: 6px; font-size: 0.9rem; }
                .compact-notes-card .compact-add-btn { width: 120px; flex: none; }
                .compact-notes-card .compact-notes-form, .compact-notes-card .compact-notes-form * { min-width: 0; box-sizing: border-box; }
                .compact-notes-card input[type="datetime-local"] { min-width: 0; flex: 1 1 auto; }
                .modern-input:focus { outline: none; border-color: var(--primary-green); box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1); }
                .btn { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-size: 0.9rem; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
                .btn-primary { background: var(--primary-green); color: white; width: 100%; }
                .btn-outline { background:var(--primary-green); border: 1px solid var(--primary-green); color: white; width: 100%; }
                .btn-danger { background: #fee2e2; color: #dc2626; width: auto; padding: 0.4rem 0.8rem; font-size: 0.8rem; }
                
                .table-container { background: white; border-radius: 12px; box-shadow: var(--card-shadow); overflow-x: scroll; overflow-y: auto; border: 1px solid var(--border-color); margin-bottom: 30px; max-height: 65vh; }
                .modern-table { width: 100%; border-collapse: collapse; min-width: 800px; }
                .modern-table th { background: #f8f9fa; padding: 1rem; text-align: center; font-weight: 600; color: var(--text-dark); border-bottom: 2px solid var(--border-color); white-space: nowrap; }
                .modern-table td { padding: 0.5rem; border-bottom: 1px solid var(--border-color); text-align: center; color: var(--text-dark); }
                .cell-input { width: 100%; padding: 8px; border: 1px solid transparent; border-radius: 4px; text-align: center; font-weight: 500; background: transparent; }
                .cell-input:focus { background: white; border-color: var(--primary-green); outline: none; }
                
                .analytics-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
                .stat-card { background: white; border-radius: 12px; padding: 24px; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); border-top: 4px solid var(--primary-green); display: flex; flex-direction: column; justify-content: space-between; min-height: 200px; }
                .stat-grid-inner { display: grid; grid-template-columns: 1fr 1fr; column-gap: 20px; row-gap: 16px; margin-bottom: 20px; }
                .stat-item { display: flex; flex-direction: column; }
                .stat-label { font-size: 0.75rem; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
                .stat-value { font-size: 1.25rem; font-weight: 700; color: var(--text-dark); }
                .stat-value.green { color: var(--primary-green); }
                .stat-value.red { color: #e74c3c; }
                .submission-count { margin-top: auto; padding-top: 15px; border-top: 1px dashed #eee; font-size: 0.85rem; color: var(--text-light); display: flex; align-items: center; gap: 8px; font-weight: 500; }
                
                .loading-overlay { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: var(--text-light); }
                .spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid var(--primary-green); border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @media print { .no-print { display: none !important; } .subject-detail-wrapper { margin: 0; padding: 0; } }

                /* --- MOBILE RESPONSIVE FIXES --- */
                @media screen and (max-width: 768px) {
                    .subject-detail-wrapper {
                        margin-left: 0;
                        padding: 10px;
                        width: 100%;
                    }
                    .page-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                        padding: 1rem;
                    }
                    .page-header h1 {
                        font-size: 1.2rem;
                        display: flex;
                        flex-direction: column;
                        gap: 5px;
                    }
                    .page-header > div { width: 100%; }
                    .badge { margin-left: 0; align-self: flex-start; }
                    .term-tabs {
                        width: 100%;
                        overflow-x: auto;
                        padding-bottom: 5px;
                        justify-content: flex-start;
                    }
                    .term-tab {
                        white-space: nowrap;
                        flex-shrink: 0;
                        padding: 6px 16px;
                    }
                    .management-grid {
                        grid-template-columns: 1fr;
                    }
                    .page-header button {
                        width: 100%;
                        margin-bottom: 5px;
                    }
                    form[style*="display: flex"] {
                        flex-direction: column;
                        align-items: stretch !important;
                    }
                    form[style*="display: flex"] input,
                    form[style*="display: flex"] button {
                        width: 100% !important;
                        flex: none !important;
                        margin-bottom: 5px;
                    }
                    .table-container {
                        width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        margin-bottom: 1rem;
                    }
                    .modern-table {
                        min-width: 600px;
                    }
                    .analytics-grid {
                        grid-template-columns: 1fr;
                    }
                    /* Ensure compact notes card is full width on mobile */
                    .compact-notes-card { width: 100%; }
                    .compact-notes-card .compact-notes-form { flex-direction: column; }
                    
                    .compact-notes-card .compact-notes-form, .compact-notes-card .compact-notes-form * { min-width: 0; }
                    .compact-notes-card input[type="datetime-local"] { -webkit-appearance: textfield; }
                    /* smaller breakpoint adjustments */
                    @media screen and (max-width: 480px) {
                        .compact-notes-card { padding: 12px; }
                        .compact-notes-card .compact-add-btn { padding: 10px 12px; }
                        .compact-notes-card .compact-datetime { font-size: 0.9rem; }
                    }
                }
            `}</style>

            {/* Header */}
            <div className="page-header">
                <div>
                    <h1>{subjectname} <span className="badge">{subjectGrade} {subjectClass}</span></h1>
                </div>

                {/* Term Selector Tabs */}
                <div className="term-tabs no-print">
                    {[1, 2, 3, 4].map(term => (
                        <button
                            key={term}
                            className={`term-tab ${currentTerm === term ? 'active' : ''}`}
                            onClick={() => setCurrentTerm(term)}
                        >
                            Term {term}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                   
                    <button className="btn btn-danger no-print" onClick={handleDeleteAll}>
                        <Icons.Trash /> Reset All Marks to 0
                    </button>
                </div>
            </div>


            <div className="no-print management-grid">

                {/* CARD 1: File Operations (Collapsible) */}
                <div className="modern-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 className="card-title" style={{ margin: 0 }}>Import / Export (Term {currentTerm})</h3>
                        <button
                            type="button"
                            className="btn btn-outline"
                            style={{ fontSize: '1.1rem', padding: '2px 10px', width: 40, height: '32px', marginLeft: 8 }}
                            onClick={() => setImportExportCollapsed(v => !v)}
                            aria-label={importExportCollapsed ? 'Expand Import/Export' : 'Collapse Import/Export'}
                        >
                            {importExportCollapsed ? '▸' : '▾'}
                        </button>
                    </div>
                    {!importExportCollapsed && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop:'10px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <button className="btn btn-outline" onClick={handleDownloadTemplate}>⬇ Excel Template</button>
                                <button className="btn btn-outline" onClick={handleExportExcel}>Export Report</button>
                            </div>
                            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px dashed #ced4da', textAlign: 'center' }}>
                                {csvUploading ? (
                                    <div style={{ color: 'var(--primary-green)', fontWeight: '600' }}>
                                        <div className="spinner" style={{ width: '20px', height: '20px', display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', marginBottom: 0 }}></div>
                                        {csvProgress}
                                    </div>
                                ) : (
                                    <>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#666' }}>Upload Data for Term {currentTerm}</label>
                                        <input type="file" accept=".xlsx, .xls, .csv" onChange={handleExcelUpload} className="modern-input" />
                                        <a
                                            href="/assets/marksure-add-assessment-classes.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ display: 'inline-block', marginTop: '10px', fontSize: '0.85rem', color: 'var(--dark-green)', fontWeight: '600', textDecoration: 'none' }}
                                        >
                                            Need help? Open Excel import guide (PDF)
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* CARD 2: Add Student (Collapsible) */}
                <div className="modern-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 className="card-title" style={{ margin: 0 }}>Add Student</h3>
                        <button
                            type="button"
                            className="btn btn-outline"
                            style={{ fontSize: '1.1rem', padding: '2px 10px', width: 40, height: '32px', marginLeft: 8 }}
                            onClick={() => setAddStudentCollapsed(v => !v)}
                            aria-label={addStudentCollapsed ? 'Expand Add Student' : 'Collapse Add Student'}
                        >
                            {addStudentCollapsed ? '▸' : '▾'}
                        </button>
                    </div>
                    {!addStudentCollapsed && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px',marginTop:'20px'
                         }}>
                            <input className="modern-input" placeholder="First Name" value={newStudent.firstName} onChange={e => setNewStudent({ ...newStudent, firstName: e.target.value })} />
                            <input className="modern-input" placeholder="Last Name" value={newStudent.lastName} onChange={e => setNewStudent({ ...newStudent, lastName: e.target.value })} />
                            <button className="btn btn-primary" onClick={handleAddStudent}>+ Add Student</button>
                        </div>
                    )}
                </div>

                {/* COMPACT SUBJECT NOTES CARD (next to Add Student) */}
                <div className="modern-card compact-notes-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 className="card-title" style={{ margin: 0 }}>Subject Notes</h3>
                        <a href={`/subjects/${id}/notes`} style={{ fontSize: '0.85rem', color: 'var(--primary-green)', textDecoration: 'none' }}>View all</a>
                    </div>

                    <form onSubmit={handleCreateNote} className="compact-notes-form" style={{ marginTop: '10px' }}>
                        <input className="modern-input" placeholder="Quick note..." value={noteText} onChange={e => setNoteText(e.target.value)} />
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input type="datetime-local" className="compact-datetime" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            <button className="btn btn-primary compact-add-btn" disabled={savingNote || !noteText.trim()}>{savingNote ? 'Saving...' : 'Add'}</button>
                        </div>
                    </form>

                    <div style={{ marginTop: '12px' }}>
                        {notes.slice(0,3).map(n => {
                            const s = getDueDateStatus(n.due_date);
                            return (
                                <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <div style={{ fontSize: '0.9rem', color: n.is_done ? '#999' : 'var(--text-dark)' }}>{n.note_text.substring(0, 50)}</div>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button onClick={() => handleToggleNoteDone(n.id, n.is_done)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>{n.is_done ? '☑' : '☐'}</button>
                                        <button onClick={() => { handleDeleteNote(n.id); }} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer' }}>🗑</button>
                                    </div>
                                </div>
                            );
                        })}
                        {notes.length === 0 && <div style={{ color: '#888', fontSize: '0.9rem', paddingTop: '8px' }}>No notes yet</div>}
                    </div>
                </div>

                {/* CARD 3: Manage Sections (Dynamic per Term) */}
                <div className="modern-card" style={{ gridColumn: '1 / -1' }}>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 className="card-title" style={{ margin: 0 }}>Assessment Sections (Term {currentTerm})</h3>
                        <button
                            type="button"
                            className="btn btn-outline"
                            style={{ fontSize: '1.1rem', padding: '2px 10px', Width: 200, height: '32px', marginLeft: 8 }}
                            onClick={() => setSectionsCollapsed(v => !v)}
                            aria-label={sectionsCollapsed ? 'Expand Sections' : 'Collapse Sections'}
                        >
                            {sectionsCollapsed ? '▸' : '▾'}
                        </button>
                    </div>

                    {/* Add Section Bar */}
                    {!sectionsCollapsed && (
                        <form onSubmit={handleAddSection} style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#f8f9fa', padding: '10px', borderRadius: '8px', marginBottom: '15px' }}>
                            <input className="modern-input" placeholder={`New Section`} style={{ flex: 2 }} value={newSection.section_name} onChange={e => setNewSection({ ...newSection, section_name: e.target.value })} />
                            <input type="number" className="modern-input" placeholder="Weight" style={{ flex: 1 }} value={newSection.weight} onChange={e => setNewSection({ ...newSection, weight: e.target.value })} />
                            <input type="number" className="modern-input" placeholder="Max" style={{ flex: 1 }} value={newSection.max_mark} onChange={e => setNewSection({ ...newSection, max_mark: e.target.value })} />
                            <select className="modern-input" style={{ flex: 1 }} value={newSection.category} onChange={e => setNewSection({ ...newSection, category: e.target.value })}>
                                {SECTION_CATEGORIES.map(c => <option key={c} value={c}>{c || 'Select Category'}</option>)}
                            </select>
                            <button className="btn btn-primary" style={{ width: 'auto', padding: '0.6rem 1.5rem' }} disabled={addingSection}>Add</button>
                        </form>
                    )}

                    {/* Section List */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                        {!sectionsCollapsed && (
                            <>
                                {sections.length === 0 && <p style={{ color: '#999', padding: '10px' }}>No sections for Term {currentTerm} yet.</p>}
                                {sections.map(s => (
                                    <div key={s.id} style={{ background: 'white', border: '1px solid #eee', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <div style={{ fontWeight: '600', color: 'var(--text-dark)' }}>{s.section_name}</div>
                                        <div style={{ display: 'flex', gap: '5px', fontSize: '0.8rem' }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', color: '#999' }}>Weight</label>
                                                <input className="modern-input" type="number" step="0.1" value={sectionWeights[s.id] ?? s.weight} onChange={e => handleUpdateSection(s.id, 'weight', e.target.value)} style={{ padding: '4px' }} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', color: '#999' }}>Max</label>
                                                <input className="modern-input" type="number" value={sectionMaxMarks[s.id] ?? s.max_mark} onChange={e => handleUpdateSection(s.id, 'max_mark', e.target.value)} style={{ padding: '4px' }} />
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '0.8rem', marginTop: '2px' }}>
                                            <label style={{ display: 'block', color: '#999' }}>Category</label>
                                            <select className="modern-input" style={{ padding: '4px', width: '100%' }} value={s.category || ''} onChange={e => handleUpdateSectionCategory(s.id, e.target.value)}>
                                                {SECTION_CATEGORIES.map(c => <option key={c} value={c}>{c || '-- None --'}</option>)}
                                            </select>
                                        </div>
                                        <button onClick={() => handleDeleteSection(s.id, s.section_name)} style={{ background: 'rgb(231, 76, 60)', border: 'none',borderRadius:'20px',width:'fit-content',color:'#ffffff', fontSize: '0.9rem', cursor: 'pointer', textAlign: 'right', marginTop: '5px',padding:'5px' }}>Delete</button>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {/* Copy All Assessments Button */}
                    {sections.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                            <button 
                                onClick={handleOpenCopyModal}
                                style={{
                                    padding: '8px 14px',
                                    background: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: '500'
                                }}
                            >
                                 Copy All Assessment Sections
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Copy All Assessments Modal */}
            {showCopyModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(44,62,80,0.18)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        margin: '0 auto',
                        width: '100%',
                        maxWidth: '500px',
                        padding: '24px',
                        borderRadius: '14px',
                        background: '#fff',
                        position: 'relative',
                        border: '1.5px solid #4CAF50',
                        boxSizing: 'border-box',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0 }}>Copy All Assessments To...</h3>
                            <button onClick={() => setShowCopyModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999' }}>✕</button>
                        </div>
                        
                        <p style={{ color: '#666', marginBottom: '16px', fontSize: '0.95rem' }}>
                            Select a subject to copy all {sections.length} assessment(s) sections from <strong>{subjectname}</strong> {subjectClass && <span>({subjectClass})</span>}.
                        </p>
                        
                        {otherSubjects.length === 0 ? (
                            <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No other subjects available.</p>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                {otherSubjects.map(subj => (
                                    <button
                                        key={subj.subject_id}
                                        onClick={() => {
                                            handleCopyAllAssessments(subj.subject_id);
                                        }}
                                        disabled={copyingSection}
                                        style={{
                                            padding: '12px 16px',
                                            background: '#f5f5f5',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            cursor: copyingSection ? 'not-allowed' : 'pointer',
                                            textAlign: 'left',
                                            fontSize: '0.95rem',
                                            opacity: copyingSection ? 0.6 : 1,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <strong>{subj.name}</strong> {subj.class_name && <span>({subj.class_name})</span>} <span style={{ color: '#999', fontSize: '0.85rem' }}>Grade {subj.grade}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Main Data Table */}
            {loading ? (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Loading Term {currentTerm} data...</p>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', margin: '10px 0' }}>
                            <button
                                className="btn btn-outline"
                                style={{ fontSize: '0.92rem', padding: '4px 14px', height: '32px', lineHeight: 1.2 }}
                                onClick={() => setShowTop10(v => !v)}
                            >
                                {showTop10 ? 'Hide' : 'Show'} Top 10 Learners
                            </button>
                            <button
                                className="btn btn-outline"
                                style={{
                                    fontSize: '0.92rem',
                                    padding: '4px 14px',
                                    height: '32px',
                                    lineHeight: 1.2,
                                    border: '1.5px solid #e74c3c',
                                    color: '#ffffff',
                                    background: '#e74c3c'
                                }}
                                onClick={() => setShowBottom10(v => !v)}
                            >
                                {showBottom10 ? 'Hide' : 'Show'} At Risk Learners
                            </button>
                        </div>

                        {/* Top 10 Modal */}
                        {showTop10 && (
                            <div style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                background: 'rgba(44,62,80,0.18)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <div className="stat_card" style={{
                                    margin: 0,
                                    maxWidth: '420px',
                                    minWidth: '260px',
                                    padding: '32px 32px 24px 32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '18px',
                                    boxShadow: '0 8px 32px rgba(44,62,80,0.18)',
                                    border: '1.5px solid var(--primary-green)',
                                    borderRadius: '18px',
                                    background: '#fff',
                                    position: 'relative',
                                }}>
                                    <button
                                        onClick={() => setShowTop10(false)}
                                        style={{
                                            position: 'absolute',
                                            top: 12,
                                            right: 12,
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.5rem',
                                            color: '#aaa',
                                            cursor: 'pointer',
                                            zIndex: 2
                                        }}
                                        aria-label="Close Top 10"
                                    >
                                        ×
                                    </button>
                                    <div className="icon_wrapper green" style={{ marginRight: '12px' }}>
                                        <Icons.Chart />
                                    </div>
                                    <div className="card_content" style={{ flex: 1 }}>
                                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-dark)', fontWeight: 700, letterSpacing: '0.2px', textAlign: 'center' }}>Top 10 Learners</h3>
                                        <ol style={{ margin: '18px 0 0 20px', padding: 0, textAlign: 'left' }}>
                                            {top10Students.map((s, i) => (
                                                <li key={s.student_id} style={{ marginBottom: '7px', fontWeight: 500, color: 'var(--text-grey)', fontSize: '1.05rem', display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ color: 'var(--text-dark)', fontWeight: 600, minWidth: 32 }}>{i + 1}. {s.first_name} {s.last_name}</span>
                                                    <span style={{ marginLeft: 12, color: 'var(--primary-green)', fontWeight: 700, fontSize: '1.08rem' }}>
                                                        {s.rawMaxSum > 0 ? Math.round((s.rawMarkSum / s.rawMaxSum) * 100) : '-'}%
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* At Risk Learners Modal (55% and below) */}
                        {showBottom10 && (
                            <div style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                background: 'rgba(44,62,80,0.18)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <div className="stat_card" style={{
                                    margin: 0,
                                    maxWidth: '420px',
                                    minWidth: '260px',
                                    padding: '32px 32px 24px 32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '18px',
                                    border: '1.5px solid #e74c3c',
                                    borderRadius: '18px',
                                    background: '#fff',
                                    position: 'relative',
                                }}>
                                    <button
                                        onClick={() => setShowBottom10(false)}
                                        style={{
                                            position: 'absolute',
                                            top: 12,
                                            right: 12,
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.5rem',
                                            color: '#aaa',
                                            cursor: 'pointer',
                                            zIndex: 2
                                        }}
                                        aria-label="Close At Risk Learners"
                                    >
                                        ×
                                    </button>
                                    <div className="icon_wrapper" style={{ marginRight: '12px', color: '#e74c3c' }}>
                                        <Icons.Chart />
                                    </div>
                                    <div className="card_content" style={{ flex: 1 }}>
                                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#e74c3c', fontWeight: 700, letterSpacing: '0.2px', textAlign: 'center' }}>At Risk Learners (≤ 55%)</h3>
                                        {atRiskLearners.length === 0 ? (
                                            <div style={{ color: '#888', textAlign: 'center', marginTop: 16 }}>No learners at or below 55%.</div>
                                        ) : (
                                            <>
                                            <ol style={{ margin: '18px 0 0 20px', padding: 0, textAlign: 'left' }}>
                                                {atRiskLearners.map((s, i) => (
                                                    <li key={s.student_id} style={{ marginBottom: '7px', fontWeight: 500, color: 'var(--text-grey)', fontSize: '1.05rem', display: 'flex', alignItems: 'center' }}>
                                                        <span style={{ color: 'var(--text-dark)', fontWeight: 600, minWidth: 32 }}>{i + 1}. {s.first_name} {s.last_name}</span>
                                                        <span style={{ marginLeft: 12, color: '#e74c3c', fontWeight: 700, fontSize: '1.08rem' }}>
                                                            {s.rawMaxSum > 0 ? Math.round((s.rawMarkSum / s.rawMaxSum) * 100) : '-'}%
                                                        </span>
                                                    </li>
                                                ))}
                                            </ol>
                                            <div style={{ marginTop: 24, textAlign: 'center' }}>
                                                <button className="btn btn-outline" style={{ borderColor: '#e74c3c', color: '#e74c3c', fontWeight: 600 }}
                                                    onClick={() => { setShowResourceModal(true); fetchResourceSuggestions(); }}>
                                                    Show Suggested Interventions & Resources
                                                </button>
                                            </div>
                                            </>
                                        )}
                                    </div>
                                                        {/* Resource Suggestions Modal */}
                                                        {showResourceModal && (
                                                            <div style={{
                                                                position: 'fixed',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                background: 'rgba(44,62,80,0.18)',
                                                                zIndex: 2000,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                padding: '20px'
                                                            }}>
                                                                <div style={{
                                                                    margin: '0 auto',
                                                                    width: '100%',
                                                                    maxWidth: '680px',
                                                                    padding: '24px',
                                                                    borderRadius: '14px',
                                                                    background: '#fff',
                                                                    position: 'relative',
                                                                    border: '1.5px solid #e74c3c',
                                                                    boxSizing: 'border-box',
                                                                    maxHeight: '90vh',
                                                                    overflowY: 'auto'
                                                                }}>
                                                                    <button
                                                                        onClick={() => setShowResourceModal(false)}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: 12,
                                                                            right: 12,
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            fontSize: '1.5rem',
                                                                            color: '#aaa',
                                                                            cursor: 'pointer',
                                                                            zIndex: 2
                                                                        }}
                                                                        aria-label="Close Resource Suggestions"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#e74c3c', fontWeight: 700, textAlign: 'center' }}>Suggested Interventions & Resources</h3>
                                                                    <div style={{ marginTop: 18 }}>
                                                                        {loadingResources ? (
                                                                            <div style={{ color: '#888', textAlign: 'center', margin: 16 }}>Loading resources...</div>
                                                                        ) : (
                                                                            resourceSuggestions.length === 0 ? (
                                                                                <div style={{ color: '#888', textAlign: 'center', margin: 16 }}>No resources found for this subject/grade.</div>
                                                                            ) : (
                                                                                <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                                                                                    {resourceSuggestions.map(res => (
                                                                                        <li key={res.id} style={{ marginBottom: 18, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
                                                                                            <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.05rem' }}>{res.title}</div>
                                                                                            <div style={{ fontSize: '0.92rem', color: '#666', margin: '4px 0 6px 0' }}>Grade {res.grade_level} | {res.subject}</div>
                                                                                            <a href={`https://marksure.tech/${res.file_path}`} target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50', fontWeight: 600, textDecoration: 'underline', fontSize: '0.97rem' }}>Download Resource</a>
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                </div>
                            </div>
                        )}
                        <span style={{ fontWeight: '600', color: '#666', padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>To search for a learner press Ctrl+F, for macOS,Cmd+F</span>
                        <div style={{ padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: '600', color: '#666' }}>Term {currentTerm} Marks</span>


                            <button className="btn btn-primary no-print" style={{ width: 'auto' }} onClick={handleBulkSave} disabled={saveAllDisabled}>
                                <Icons.Save /> {saveAllDisabled ? 'Saving...' : 'Save Changes'}
                            </button>

                        </div>



                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', paddingLeft: '1.5rem', width: '200px' }}>Student Name</th>
                                    {sections.map(s => (
                                        <th key={s.id}>
                                            <div style={{ fontSize: '0.95rem' }}>{s.section_name}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '400' }}>Max: {s.max_mark}</div>
                                        </th>
                                    ))}
                                    <th style={{ width: '100px', background: '#e8f5e9', borderLeft: '2px solid #dfe6e9' }}>Raw Total</th>
                                    <th style={{ width: '100px', background: '#fff3e0', borderLeft: '1px solid #dfe6e9' }}>Wgt Total</th>
    
                                    <th style={{ width: '80px', background: '#e8f5e9' }}>%</th>
                                    <th style={{ width: '60px', background: '#e8f5e9' }}>Lvl</th>
                                    <th className="no-print" style={{ width: '60px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentsWithComputed.map(student => (
                                    
                                    <tr key={student.student_id}>
                                        <td style={{ textAlign: 'left', paddingLeft: '1.5rem', fontWeight: '500' }}>
                                            {student.first_name} {student.last_name}
                                        </td>
                                        {sections.map(s => {
                                            const key = `${student.student_id}-${s.id}`;
                                            const markObj = marksMap.get(key);
                                            const val = marksBeingEdited[key] !== undefined ? marksBeingEdited[key] : (markObj?.mark ?? '');
                                            const ref = getMarkInputRef(s.id, student.student_id);
                                          
                                            return (
                                                <td key={s.id} style={{ padding: 0 }}>
                                                    <input
                                                        ref={ref}
                                                        className="cell-input"
                                                        value={val}
                                                        placeholder="-"
                                                        onChange={e => handleSectionMarkChange(student.student_id, s.id, e.target.value)}
                                                        onBlur={() => handleMarkBlur(student.student_id, s.id)}
                                                        onKeyDown={e => handleGridNavigation(e, student.student_id, s.id)}
                                                    />
                                                </td>
                                            );
                                        })}

                                        {/* Raw Total */}
                                        <td style={{ fontWeight: 'bold', color: '#555', background: '#f8f9fa', borderLeft: '2px solid #dfe6e9' }}>
                                            {student.rawMaxSum > 0 ? (
                                                <span>{Number(student.rawMarkSum).toFixed(1)} <span style={{ fontSize: '0.8em', color: '#999' }}>/ {student.rawMaxSum}</span></span>
                                            ) : '-'}
                                        </td>

                                        {/* Weighted Total */}
                                        <td style={{ fontWeight: 'bold', color: '#e67e22', background: '#fff8e1', borderLeft: '1px solid #dfe6e9' }}>
                                            {student.totalWeight > 0 ? (
                                                <span>
                                                    {Number(student.weightedScore).toFixed(1)}
                                                    <span style={{ fontSize: '0.8em', color: '#999' }}> / {student.totalWeight}</span>
                                                </span>
                                            ) : '-'}
                                        </td>

                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold', color: student.rawMaxSum > 0 && (student.rawMarkSum / student.rawMaxSum) * 100 >= 50 ? 'var(--primary-green)' : '#e74c3c' }}>
                                            {student.rawMaxSum > 0 ? Math.round((student.rawMarkSum / student.rawMaxSum) * 100) + '%' : '-'}
                                            {top10Ids.includes(student.student_id) && <span className="top-badge" title="Top 10">⭐</span>}
                                        </td>

                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold' }}>
                                            <span style={{
                                                display: 'inline-block', width: '24px', height: '24px', lineHeight: '24px', borderRadius: '50%',
                                                background: student.level >= 4 ? 'var(--light-green)' : '#fee2e2',
                                                color: student.level >= 4 ? 'var(--dark-green)' : '#dc2626',
                                                fontSize: '0.85rem'
                                            }}>
                                                {formatGrade(student.level)}
                                            </span>
                                        </td>
                                        <td className="no-print">
                                            <button className="btn-danger" style={{ borderRadius: '4px', padding: '4px 8px' }} onClick={() => handleDeleteStudent(student.student_id, student.first_name)}>×</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Performance Analytics (Dynamic per Term) */}
                    {sections.length > 0 && (
                        <div className="no-print" style={{ marginTop: '40px' }}>
                            <h3 className="stats-section-title"><Icons.Chart /> Term {currentTerm} Analytics</h3>
                            <div className="analytics-grid">
                                {sections.map(section => {
                                    const stats = getSectionStats(section);
                                    if (!stats) return null;

                                    return (
                                        <div key={section.id} className="stat-card">
                                            <h4>{section.section_name}</h4>
                                            <div className="stat-grid-inner">
                                                <div className="stat-item">
                                                    <span className="stat-label">Average Mark</span>
                                                    <span className="stat-value">{stats.avg.toFixed(1)}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Avg %</span>
                                                    <span className="stat-value green">{stats.avgPerc.toFixed(1)}%</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Highest</span>
                                                    <span className="stat-value">{stats.max}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Lowest</span>
                                                    <span className="stat-value red">{stats.min}</span>
                                                </div>
                                            </div>
                                            <div className="submission-count">
                                                <Icons.Check /> Submitted: <strong>{stats.count}</strong> / {students.length} students
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    
                </>
            )}
        </div>
    );
}